import React from 'react';
import { AlertTriangle, Info, CheckCircle, HelpCircle } from 'lucide-react';
import useThemeStore from '../store/themeStore';
import { ThemeProps } from '../utils/theme';
import { readableTextColour, visibleAccent } from '../utils/colourContrast';

// Themed confirm/cancel dialog shared by settings toggles and other
// destructive-action flows.

// Used when neither theme colour is usable on the white card.
const DEFAULT_BUTTON_COLOUR = '#3b82f6';

export interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  type?: 'warning' | 'info' | 'success';
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type = 'warning',
}) => {
  const getThemes = useThemeStore((state) => state.theme as unknown as ThemeProps);

  // The card is white, so a white colour 1 would hide the buttons entirely:
  // prefer colour 1, fall back to colour 2, then a dark default (and the
  // reverse for the hover state).
  const buttonColour = visibleAccent(
    getThemes?.color1 || '',
    getThemes?.color2,
    DEFAULT_BUTTON_COLOUR
  );
  const hoverColour = visibleAccent(
    getThemes?.color2 || '',
    getThemes?.color1,
    DEFAULT_BUTTON_COLOUR
  );
  const buttonStyle = {
    backgroundColor: buttonColour,
    color: readableTextColour(buttonColour),
  };

  const handleHover = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const background = e.type === 'mouseover' ? hoverColour : buttonColour;
    e.currentTarget.style.background = background;
    e.currentTarget.style.color = readableTextColour(background);
  };

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="w-8 h-8 text-black" />;
      case 'info':
        return <Info className="w-8 h-8 text-black" />;
      case 'success':
        return <CheckCircle className="w-8 h-8 text-black" />;
      default:
        return <HelpCircle className="w-8 h-8 text-black" />;
    }
  };

  // The component returns null when closed, so nothing here ever renders with
  // isOpen false. The previous open/closed style ternaries were therefore dead
  // branches, and the fade/scale transitions could never play — a CSS
  // transition animates a change from a painted value, and this element mounts
  // already at its final state. The modal has always appeared instantly; that
  // behaviour is unchanged, the dead declarations are simply gone.
  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[1001]"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl"
        style={{ backgroundColor: 'white' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center mb-4">
          <div className="mr-3">{getIcon()}</div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md transition-all duration-200"
            style={buttonStyle}
            onMouseOver={handleHover}
            onMouseOut={handleHover}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-md transition-all duration-200"
            style={buttonStyle}
            onMouseOver={handleHover}
            onMouseOut={handleHover}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
